import ApiService, { IRequestParameters } from '@/plugins/api-plugin/services/api.service'
import { PageNumberPagination } from '@/interfaces/common/PageNumberPagination'
import PortfolioItemMarketDetailDao from '@/interfaces/portfolio/PortfolioItemMarketDetail.dao'
import AsinDao from '@/interfaces/portfolio/Asin.dao'
import SellerDao from '@/interfaces/portfolio/Seller.dao'
import BuyBoxSellerDistributionDao from '@/interfaces/portfolio/BuyBoxSellerDistribution.dao'
import GroupDao from '@/interfaces/portfolio/Group.dao'
import PortfolioItemMarketDetailsMetadataDto from '@/interfaces/portfolio/PortfolioItemMarketDetailsMetadata.dto'
import PortfolioImportProductDao from '@/interfaces/portfolio/PortfolioImportProduct.dao'
import { AxiosResponse } from 'axios'
import PortfolioImportGroupDao from '@/interfaces/portfolio/PortfolioImportGroup.dao'
import MasterGroupDao from '@/interfaces/portfolio/MasterGroup.dao'
import MasterGroupDto from '@/interfaces/portfolio/MasterGroup.dto'
import { SearchCatalogItemsDto } from '@/interfaces/market-explorer/SearchCatalogItems.dto'
import { GetItemOffersDto } from '@/interfaces/market-explorer/GetItemOffers.dto'
import { GetMyFeesEstimateForASINDto } from '@/interfaces/market-explorer/GetMyFeesEstimateForASIN.dto'
import { GetItemOffersBatchDto } from '@/interfaces/market-explorer/GetItemOffersBatch.dto'
import AsinsDao from '@/interfaces/portfolio/Asins.dao'
import AsinsAnalysisDao from '@/interfaces/portfolio/AsinsAnalysis.dao'
import AsinAnalysisDao from '@/interfaces/portfolio/AsinAnalysis.dao'

export default class PortfolioService extends ApiService {
    async getPortfolioItemsCompact(
        params?: IRequestParameters,
    ): Promise<[(string | number | (string | number)[][])[][], PageNumberPagination] | null> {
        return this.request<undefined, [(string | number | (string | number)[][])[][], PageNumberPagination]>(
            'GET',
            '/portfolios/portfolio-items-compact',
            params,
            undefined,
            undefined,
            'getPortfolioItems',
        ).catch(() => null)
    }
    async getPortfolioItemMarketDetails(
        params: IRequestParameters,
        body?: any,
    ): Promise<[PortfolioItemMarketDetailDao[], PageNumberPagination] | null> {
        return this.request<undefined, [PortfolioItemMarketDetailDao[], PageNumberPagination]>(
            'GET',
            '/portfolios/portfolio-item-market-details',
            params,
            body,
            undefined,
            'getPortfolioItemMarketDetails',
        ).catch(() => null)
    }

    async getAsinsCompact(
        asins: string[],
        params?: IRequestParameters,
    ): Promise<[AsinDao[], PageNumberPagination] | null> {
        return this.request<string[], [AsinDao[], PageNumberPagination]>(
            'POST',
            '/portfolios/asins-compact',
            params,
            asins,
            undefined,
            'getAsinsCompact',
        ).catch(() => null)
    }

    async getAsins(params?: IRequestParameters): Promise<[AsinsDao[], PageNumberPagination] | null> {
        return this.request<string[], [AsinsDao[], PageNumberPagination]>(
            'POST',
            '/portfolios/asins',
            params,
            undefined,
            undefined,
            'getAsins',
        ).catch(() => null)
    }

    async getAsinsAnalysis(params?: IRequestParameters): Promise<[AsinsAnalysisDao[], PageNumberPagination] | null> {
        return this.request<string[], [AsinsAnalysisDao[], PageNumberPagination]>(
            'GET',
            '/asin-analysis',
            params,
            undefined,
            undefined,
            'getAsinAnalysis',
        ).catch(() => null)
    }

    async getAsinAnalysis(id: string): Promise<AsinAnalysisDao | null> {
        return this.request<undefined, AsinAnalysisDao | null>(
            'GET',
            `/asin-analysis/${encodeURIComponent(id)}`,
            undefined,
            undefined,
            undefined,
            'getAsinAnalysis',
        ).catch(() => null)
    }

    async exportAsinAnalysis(params: IRequestParameters): Promise<AxiosResponse | null> {
        return this.request<undefined, AxiosResponse>(
            'POST',
            '/asin-analysis/export-asin-analysis',
            params,
            undefined,
            'blob',
            'exportAsinAnalysis',
        ).catch(() => null)
    }

    async getSellersCompact(
        params?: IRequestParameters,
    ): Promise<[(string | number)[][], PageNumberPagination] | null> {
        return this.request<undefined, [(string | number)[][], PageNumberPagination]>(
            'GET',
            '/portfolios/sellers-compact',
            params,
            undefined,
            undefined,
            'getSellersCompact',
        ).catch(() => null)
    }

    async getSellers(params?: IRequestParameters): Promise<[SellerDao[], PageNumberPagination] | null> {
        return this.request<undefined, [SellerDao[], PageNumberPagination]>(
            'GET',
            '/portfolios/sellers',
            params,
            undefined,
            undefined,
            'getSellers',
        ).catch(() => null)
    }
    async getBuyBoxSellerDistribution(params?: IRequestParameters): Promise<BuyBoxSellerDistributionDao[] | null> {
        return this.request<undefined, BuyBoxSellerDistributionDao[]>(
            'GET',
            '/portfolios/buy-box-seller-distribution',
            params,
            undefined,
            undefined,
            'getBuyBoxSellerDistribution',
        ).catch(() => null)
    }

    async getMasterGroups(params?: IRequestParameters): Promise<[MasterGroupDao[], PageNumberPagination] | null> {
        return this.request<undefined, [MasterGroupDao[], PageNumberPagination]>(
            'GET',
            '/portfolios/master-groups',
            params,
            undefined,
            undefined,
            'getMasterGroups',
        ).catch(() => null)
    }
    async deleteMasterGroup(id: number): Promise<MasterGroupDao | null> {
        return this.request<undefined, MasterGroupDao>(
            'DELETE',
            `/portfolios/master-groups/${id}`,
            undefined,
            undefined,
            undefined,
            'deleteMasterGroup',
        ).catch(() => null)
    }
    async createMasterGroup(masterGroupDto: MasterGroupDto): Promise<MasterGroupDao | null> {
        return this.request<MasterGroupDto, MasterGroupDao>(
            'POST',
            `/portfolios/master-groups`,
            undefined,
            masterGroupDto,
            undefined,
            'createMasterGroup',
        ).catch(() => null)
    }
    async updateMasterGroup(masterGroupDto: MasterGroupDto): Promise<MasterGroupDao | null> {
        return this.request<MasterGroupDto, MasterGroupDao>(
            'PUT',
            `/portfolios/master-groups`,
            undefined,
            masterGroupDto,
            undefined,
            'createMasterGroup',
        ).catch(() => null)
    }
    async updatePortfolioMinSellPrice(Id: number, MinSellPrice: number): Promise<{ MinSellPrice: number } | null> {
        return this.request<{ MinSellPrice: number }, { MinSellPrice: number }>(
            'PATCH',
            `/portfolios/update-min-sell-price/${Id}`,
            undefined,
            { MinSellPrice },
            undefined,
            'updatePortfolioMinSellPrice',
        ).catch(() => null)
    }
    async updatePortfolioPriceFloor(
        Id: number,
        PriceFloor: number | null,
    ): Promise<{ PriceFloor: number | null } | null> {
        return this.request<{ PriceFloor: number | null }, { PriceFloor: number | null }>(
            'PATCH',
            `/portfolios/update-price-floor/${Id}`,
            undefined,
            { PriceFloor },
            undefined,
            'updatePortfolioPriceFloor',
        ).catch(() => null)
    }
    async updatePortfolioDemandPriceCap(
        Id: number,
        DemandPriceCap: number | null,
    ): Promise<{ DemandPriceCap: number | null } | null> {
        return this.request<{ DemandPriceCap: number | null }, { DemandPriceCap: number | null }>(
            'PATCH',
            `/portfolios/update-demand-price-cap/${Id}`,
            undefined,
            { DemandPriceCap },
            undefined,
            'updatePortfolioDemandPriceCap',
        ).catch(() => null)
    }
    async getGroups(params?: IRequestParameters): Promise<[GroupDao[], PageNumberPagination] | null> {
        return this.request<undefined, [GroupDao[], PageNumberPagination]>(
            'GET',
            '/portfolios/groups',
            params,
            undefined,
            undefined,
            'getGroups',
        ).catch(() => null)
    }
    async getPortfolioItemMarketDetailsMetadata(
        params?: IRequestParameters,
    ): Promise<PortfolioItemMarketDetailsMetadataDto | null> {
        return this.request<undefined, PortfolioItemMarketDetailsMetadataDto>(
            'GET',
            '/portfolios/portfolio-item-market-details-metadata',
            params,
            undefined,
            undefined,
            'getPortfolioItemMarketDetailsMetadata',
        ).catch(() => null)
    }
    async importProducts(importProducts: PortfolioImportProductDao[]): Promise<PortfolioImportProductDao[] | null> {
        return this.request<PortfolioImportProductDao[], any[]>(
            'POST',
            '/portfolios/import-products',
            undefined,
            importProducts,
            undefined,
            'importProducts',
        ).catch(() => null)
    }
    async importGroups(importGroups: PortfolioImportGroupDao[]): Promise<PortfolioImportGroupDao[] | null> {
        return this.request<PortfolioImportGroupDao[], any[]>(
            'POST',
            '/portfolios/import-groups',
            undefined,
            importGroups,
            undefined,
            'importGroups',
        ).catch(() => null)
    }
    async deletePortfolioItems(ids: number[]): Promise<boolean | null> {
        return this.request<number[], boolean>(
            'DELETE',
            '/portfolios/portfolio-items',
            undefined,
            ids,
            undefined,
            'deletePortfolioItems',
        ).catch(() => null)
    }
    async exportProducts(params: IRequestParameters): Promise<AxiosResponse | null> {
        return this.request<undefined, AxiosResponse>(
            'POST',
            '/portfolios/export-products',
            params,
            undefined,
            'blob',
            'exportProducts',
        ).catch(() => null)
    }
    async exportGroups(params: IRequestParameters): Promise<AxiosResponse | null> {
        return this.request<undefined, AxiosResponse>(
            'POST',
            '/portfolios/export-groups',
            params,
            undefined,
            'blob',
            'exportGroups',
        ).catch(() => null)
    }

    async searchCatalogItems(payload: SearchCatalogItemsDto): Promise<any> {
        return this.request<SearchCatalogItemsDto, any>(
            'POST',
            '/market-explorer/search-catalog-items',
            undefined,
            payload,
            undefined,
            'searchCatalogItems',
        ).catch(() => null)
    }

    async getItemOffers(payload: GetItemOffersDto): Promise<any> {
        return this.request<GetItemOffersDto, any>(
            'POST',
            '/market-explorer/get-item-offers',
            undefined,
            payload,
            undefined,
            'getItemOffers',
        ).catch(() => null)
    }

    async getItemOffersBatch(payload: GetItemOffersBatchDto): Promise<any> {
        return this.request<GetItemOffersBatchDto, any>(
            'POST',
            '/market-explorer/get-item-offers-batch',
            undefined,
            payload,
            undefined,
            'getItemOffersBatch',
        ).catch(() => null)
    }

    async getMyFeesEstimateForASIN(payload: GetMyFeesEstimateForASINDto): Promise<any> {
        return this.request<GetMyFeesEstimateForASINDto, any>(
            'POST',
            '/market-explorer/get-my-fees-estimate-for-asin',
            undefined,
            payload,
            undefined,
            'getMyFeesEstimateForASIN',
        ).catch(() => null)
    }
}
